import { Link } from "@tanstack/react-router";

import {
  type Article,
  articleBySlug,
  outline,
  readingMinutes,
} from "../lib/content";
import { Contents, FaqList, renderBlock, ShortAnswer } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * Renders one article.
 *
 * The blocks themselves are rendered by `ProseBlocks`, which quizzes share.
 * What lives here is the article's own furniture: the dateline, the standfirst,
 * and the "read next" cards.
 *
 * Line length is capped at `max-w-[68ch]`. Roughly 65–75 characters is the
 * span the eye can return along without losing its place; a full-width column
 * on a laptop is about 140, which is why long pages on wide screens feel
 * tiring in a way people rarely attribute to the layout.
 */

/** UTC so the printed date matches `datePublished` in the schema exactly. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function Related({ slugs }: { slugs: string[] }) {
  const articles = slugs.map(articleBySlug).filter((a) => a !== undefined);
  if (articles.length === 0) return null;
  return (
    <section className="mt-14 border-t-[2.5px] border-ink pt-10">
      <h2 className="font-display text-2xl font-bold text-ink">Read next</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {articles.map((a) => (
          <li key={a.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: a.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {a.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {a.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ArticleBody({ article }: { article: Article }) {
  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {formatDate(article.published)}
        {article.updated ? ` · updated ${formatDate(article.updated)}` : ""} ·{" "}
        {readingMinutes(article)} min read
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        {article.h1}
      </h1>
      <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {article.standfirst}
      </p>

      {/* The answer before the argument. First thing under the standfirst,
          because a reader who only wants it should not have to scroll, and
          because an assistant looking for a passage to quote takes whatever is
          nearest the top and most self-contained. */}
      <ShortAnswer takeaways={article.takeaways} />

      <Contents headings={outline(article)} />

      {article.body.map(renderBlock)}

      {article.faq ? <FaqList items={article.faq} /> : null}

      {/* The one conversion point on the page, and it sits after the argument
          rather than interrupting it. An article that sells in paragraph two is
          an article nobody finishes, and an unfinished article converts nobody. */}
      <StoreCta from={`article:${article.slug}`} />

      <Related slugs={article.related} />
    </article>
  );
}
